import { useCallback, useEffect, useRef, useState } from 'react';

const COPIED_RESET_MS = 1500;

export interface UseCopyToClipboardResult {
  isCopied: boolean;
  copyToClipboard: () => Promise<void>;
}

export function useCopyToClipboard(text: string): UseCopyToClipboardResult {
  const resetTimerRef = useRef<ReturnType<typeof setTimeout>>();
  const [isCopied, setIsCopied] = useState(false);

  useEffect(() => {
    return () => {
      if (resetTimerRef.current) {
        clearTimeout(resetTimerRef.current);
      }
    };
  }, []);

  useEffect(() => {
    setIsCopied(false);
    if (resetTimerRef.current) {
      clearTimeout(resetTimerRef.current);
    }
  }, [text]);

  const copyToClipboard = useCallback(async () => {
    let copySucceeded = false;

    try {
      await navigator.clipboard.writeText(text);
      copySucceeded = true;
    } catch {
      const tempTextarea = document.createElement('textarea');
      tempTextarea.value = text;
      tempTextarea.setAttribute('readonly', '');
      tempTextarea.style.position = 'absolute';
      tempTextarea.style.left = '-9999px';
      document.body.appendChild(tempTextarea);
      tempTextarea.select();
      const execCopyResult = document.execCommand('copy');
      document.body.removeChild(tempTextarea);
      copySucceeded = execCopyResult === true;
    }

    if (copySucceeded) {
      setIsCopied(true);
      if (resetTimerRef.current) {
        clearTimeout(resetTimerRef.current);
      }
      resetTimerRef.current = setTimeout(() => {
        setIsCopied(false);
      }, COPIED_RESET_MS);
    }
  }, [text]);

  return { isCopied, copyToClipboard };
}
